import { useEffect, useState, useRef, useMemo } from 'react';
import { useAppStore } from '@/stores/appStore';
import { Navbar } from '@/components/layout/Navbar';
import { BatchListView } from '@/components/list/BatchListView';
import { FilterBar } from '@/components/filters/FilterBar';
import { Button } from '@/components/ui/Button';
import { BatchUpdateDialog } from '@/components/modals/BatchUpdateDialog';
import { TimeframeMenu } from '@/components/ui/TimeframeMenu';
import { isDateMode } from '@/components/timeline/TimelineWeekView';
import { useFieldConfig } from '@/hooks/useFieldConfig';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import {
  productApi,
  subProductApi,
  initiativeApi,
  classificationApi,
  statusApi,
  tagApi,
  userApi,
  fiscalYearApi,
  fieldConfigApi,
  labsApi,
} from '@/services/api';

type ViewMode = 'weekly' | 'monthly' | 'quarterly';
const TIMEFRAMES: { value: ViewMode; label: string }[] = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
];
const VIEW_MODE_KEY = 'cpr_batch_update_view_mode';
const SKIPPED_HANDOFF_KEY = 'cp_batch_skipped_handoff';
const BATCH_MAX = 250;

// One-time read of the skipped-rows handoff written by the Batch Update result
// modal before it opens this page in a new tab. Cached at module level so React
// Strict Mode's double-invoked initializer returns the same value both times
// (the localStorage key is removed on the first real read).
let _skippedCache: { value: { ids: Set<string>; reason: string } | null } | undefined;

function readSkippedHandoff(): { ids: Set<string>; reason: string } | null {
  if (_skippedCache !== undefined) {
    const { value } = _skippedCache;
    _skippedCache = undefined;
    return value;
  }
  try {
    const raw = localStorage.getItem(SKIPPED_HANDOFF_KEY);
    if (!raw) return null;
    localStorage.removeItem(SKIPPED_HANDOFF_KEY);
    const parsed = JSON.parse(raw) as { ids?: string[]; reason?: string };
    if (!parsed.ids || parsed.ids.length === 0) {
      _skippedCache = { value: null };
      return null;
    }
    const value = { ids: new Set(parsed.ids), reason: parsed.reason ?? '' };
    _skippedCache = { value };
    return value;
  } catch {
    return null;
  }
}

export function BatchUpdatePage() {
  const {
    initiatives,
    setProducts,
    setInitiatives,
    setClassifications,
    setStatuses,
    setTags,
    setOwners,
    setQuarters,
    setFieldConfigs,
    setLabsFeatures,
    setLoading,
    setError,
    refreshTrigger,
    triggerRefresh,
  } = useAppStore();
  const fc = useFieldConfig();

  const [filteredInitiatives, setFilteredInitiatives] = useState<any[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);
  const viewContainerRef = useRef<HTMLDivElement>(null);
  const [skippedHandoff, setSkippedHandoff] = useState(() => readSkippedHandoff());
  const preselectedRef = useRef(false);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const stored = sessionStorage.getItem(VIEW_MODE_KEY);
    return (stored === 'weekly' || stored === 'monthly' || stored === 'quarterly') ? stored : 'quarterly';
  });

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    sessionStorage.setItem(VIEW_MODE_KEY, mode);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [
        productsData,
        subProductsData,
        initiativesData,
        classificationsData,
        statusesData,
        tagsData,
        usersData,
        fiscalYearsData,
        fieldConfigData,
        labsData,
      ] = await Promise.all([
        productApi.getAll(),
        subProductApi.getAll(),
        initiativeApi.getAll(),
        classificationApi.getAll(),
        statusApi.getAll(),
        tagApi.getAll(),
        userApi.getAll(),
        fiscalYearApi.getAll(),
        fieldConfigApi.getAll().catch(() => []),
        labsApi.getAll().catch(() => []),
      ]);

      const productsWithSubProducts = productsData.map((product: any) => ({
        ...product,
        subProducts: subProductsData.filter((sp: any) => sp.productId === product.id),
      }));

      setProducts(productsWithSubProducts);
      setInitiatives(initiativesData);
      setClassifications(classificationsData);
      setStatuses(statusesData);
      setTags(tagsData);
      setOwners(usersData);
      if (fieldConfigData.length > 0) setFieldConfigs(fieldConfigData);
      if (labsData.length > 0) setLabsFeatures(labsData);

      const allQuarters = fiscalYearsData
        .flatMap((fy: any) => fy.quarters || [])
        .sort((a: any, b: any) => a.displayOrder - b.displayOrder);
      setQuarters(allQuarters);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load roadmap data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [refreshTrigger]);

  // In skipped mode, show only the skipped rows pulled from the full initiative
  // list (bypassing FilterBar) so they appear regardless of saved/default filters.
  const displayedInitiatives = useMemo(
    () =>
      skippedHandoff ? initiatives.filter((i) => skippedHandoff.ids.has(i.id)) : filteredInitiatives,
    [skippedHandoff, initiatives, filteredInitiatives]
  );

  // Pre-select the skipped rows once they are available (run once).
  useEffect(() => {
    if (!skippedHandoff || preselectedRef.current) return;
    if (displayedInitiatives.length === 0) return;
    setSelectedIds(new Set(displayedInitiatives.map((i) => i.id)));
    preselectedRef.current = true;
  }, [skippedHandoff, displayedInitiatives]);

  const clearSkipped = () => {
    setSkippedHandoff(null);
    setSelectedIds(new Set());
    preselectedRef.current = false;
  };

  // Drop any selected ids that are no longer in the displayed set
  useEffect(() => {
    setSelectedIds((prev) => {
      const visible = new Set(displayedInitiatives.map((i) => i.id));
      const next = new Set<string>();
      prev.forEach((id) => {
        if (visible.has(id)) next.add(id);
      });
      return next.size === prev.size ? prev : next;
    });
  }, [displayedInitiatives]);

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(displayedInitiatives.map((i) => i.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  const selectedCount = selectedIds.size;
  const overLimit = selectedCount > BATCH_MAX;

  const selectedIdList = useMemo(() => Array.from(selectedIds), [selectedIds]);

  const handleComplete = () => {
    clearSelection();
    triggerRefresh();
  };

  const itemLabelLower = fc.label('initiative').toLowerCase();

  return (
    <div className="h-screen flex flex-col bg-background-primary">
      <Navbar />

      <main className="flex-1 overflow-hidden">
        <div className="h-full flex flex-col">
          <div className="px-3 sm:px-6 py-3 bg-background-primary flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
            <div className={`flex-1 min-w-0 ${skippedHandoff ? 'hidden' : ''}`}>
              <FilterBar
                onFilteredInitiativesChange={setFilteredInitiatives}
                viewType="list"
                viewContainerRef={viewContainerRef}
                viewMode={viewMode}
                weeklyDatedCount={viewMode === 'weekly' ? displayedInitiatives.filter(isDateMode).length : undefined}
                hideExport
              />
            </div>
            {skippedHandoff && (
              <div className="flex-1 min-w-0 flex items-center gap-3 rounded-md bg-amber-50 border border-amber-200 px-4 py-2">
                <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <div className="text-sm min-w-0">
                  <span className="font-medium text-text-primary">
                    Showing {displayedInitiatives.length} skipped{' '}
                    {displayedInitiatives.length === 1 ? itemLabelLower : itemLabelLower + 's'} from your last batch.
                  </span>
                  {skippedHandoff.reason && (
                    <span className="text-text-secondary"> Reason: {skippedHandoff.reason}.</span>
                  )}
                </div>
                <Button variant="ghost" size="sm" onClick={clearSkipped} className="flex-shrink-0">
                  Show all
                </Button>
              </div>
            )}
            <TimeframeMenu value={viewMode} options={TIMEFRAMES} onChange={handleViewModeChange} />
          </div>

          {/* Page header */}
          <div className="px-6 pt-1 pb-4 bg-background-primary">
            <h1 className="text-xl font-semibold text-text-primary">Batch Update</h1>
            <p className="text-sm text-text-secondary mt-0.5">
              Update a field across many {itemLabelLower}s at once.
            </p>
          </div>

          {/* Selection action bar */}
          <div className="px-6 py-2 border-y border-gray-200 bg-surface-subtle flex items-center gap-3 flex-shrink-0">
            {selectedCount === 0 ? (
              <span className="text-sm text-text-secondary">
                Select {itemLabelLower}s below, then choose a field to batch-update (max {BATCH_MAX} per batch).
              </span>
            ) : (
              <>
                <span className="text-sm font-medium text-text-primary">
                  {selectedCount} selected
                </span>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={overLimit}
                  onClick={() => setDialogOpen(true)}
                >
                  Batch Update
                </Button>
                <Button variant="ghost" size="sm" onClick={clearSelection}>
                  Clear
                </Button>
                {overLimit && (
                  <span className="text-sm text-red-600">
                    Too many selected — reduce to {BATCH_MAX} or fewer.
                  </span>
                )}
              </>
            )}
          </div>

          <div className="flex-1 overflow-hidden">
            <BatchListView
              initiatives={displayedInitiatives}
              containerRef={viewContainerRef}
              viewMode={viewMode}
              selectedIds={selectedIds}
              onToggleOne={toggleOne}
              onToggleAll={toggleAll}
            />
          </div>
        </div>
      </main>

      <BatchUpdateDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        selectedIds={selectedIdList}
        onComplete={handleComplete}
      />
    </div>
  );
}
